import qs from 'qs';
import React, { createRef, MutableRefObject, useEffect, useRef } from 'react';
import { animated, interpolate } from 'react-spring';
import { useHistory } from 'react-router-dom';

import { lerp } from '../../../../utils/math';
import { device, insets } from '../../../../utils/device';
import { coachesIcon, courtsIcon, gamesIcon, playersIcon, postsIcon } from '../../../../utils/icons';

import { AnimState } from '../../animations/animate';

import styles from './Controls.module.scss';

type Props = {
    page: any,
    query: any,
    item: any,

    anim: any,
    animStateRef: MutableRefObject<AnimState>
};

export default function Controls({ page, query, item, anim, animStateRef }: Props) {
    const row1Ref = createRef<HTMLDivElement>();
    const row2Ref = createRef<HTMLDivElement>();
    const colRef = createRef<HTMLDivElement>();
    const row1RectRef = useRef<DOMRect>();
    const row2RectRef = useRef<DOMRect>();
    const colRectRef = useRef<DOMRect>();
    const rowYRef = useRef(0);

    const history = useHistory();

    useEffect(() => {
        const w = window.innerWidth;
        const h = window.innerHeight;

        let tw = 375;
        let th = 560;
        if (!device.isShortScreen) {
            tw = 375;
            th = 640;
        }

        const scale = Math.min(w / tw, h / th);

        row1RectRef.current = row1Ref.current?.getBoundingClientRect();
        row2RectRef.current = row2Ref.current?.getBoundingClientRect();
        colRectRef.current = colRef.current?.getBoundingClientRect();

        if (row1RectRef.current && row2RectRef.current && colRectRef.current) {
            rowYRef.current = -((row2RectRef.current.height / scale) + (row1RectRef.current.height / scale)) + 30 + (colRectRef.current.height / scale);
        }
    }, [row1Ref.current, row2Ref.current]);

    function computeInterpolate<T>(states: Record<number, T>): T {
        const value = states[animStateRef.current];
        return value != null ? value : states[AnimState.None];
    }

    return (
        <div className={styles.container}>
            <div className={styles.row} ref={row1Ref}>
                <animated.div
                    className={styles.col}
                    ref={colRef}
                    style={{
                        transform: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.SearchToPlayers]: `translate(${lerp(0, -12, alpha)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), alpha)}px)`,

                            [AnimState.CourtsToPlayers]: `translate(${lerp(0, -12, alpha)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), alpha)}px)`,

                            [AnimState.Players]: `translate(${lerp(0, -12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,
                            [AnimState.PlayersCourtsToCourts]: `translate(${lerp(0, -12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,
                            [AnimState.PLayersPlayersToCourts]: `translate(${lerp(0, -12, 1 - alpha)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1 - alpha)}px)`,
                            [AnimState.PLayersPlayersToPlayers]: `translate(${lerp(0, -12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,
                            [AnimState.PLayersPlayersToPlayersBack]: `translate(${lerp(0, -12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,
                            [AnimState.PlayersPlayersToCalendarProgress]: `translate(${lerp(0, -12, 1 - alpha)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1 - alpha)}px)`,
                            [AnimState.PlayersPlayersToFilter]: `translate(${lerp(0, -12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,
                            [AnimState.PlayersCourtsToFilter]: `translate(${lerp(0, -12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,

                            [AnimState.PlayersPlayersCalendarToPlayersPlayers]: `translate(${lerp(0, -12, 1 - alpha)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1 - alpha)}px)`,
                        })),
                        cursor: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.PlayersPlayersToCalendar]: 'default',
                        })),
                    }}
                    onClick={() => {
                        if (page === 'courts' && !query.calendar) {
                            history.push({
                                search: qs.stringify({
                                    mode: 'players',
                                }),
                            });
                        }
                    }}
                    role="presentation"
                >
                    <animated.div
                        className={styles.item}
                        style={{
                            position: 'absolute',
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,

                                [AnimState.SearchToPlayers]: alpha,

                                [AnimState.CourtsToPlayers]: alpha,

                                [AnimState.Players]: 1,
                                [AnimState.PlayersCourtsToCourts]: 1 - alpha,
                                [AnimState.PLayersPlayersToPlayers]: 1,
                                [AnimState.PLayersPlayersToPlayersBack]: 1,
                                [AnimState.PLayersPlayersToCourts]: 1 - alpha,
                                [AnimState.PlayersPlayersToCalendarProgress]: 1 - alpha,
                                [AnimState.PlayersPlayersToFilter]: alpha ? 0 : 1,
                                [AnimState.PlayersCourtsToFilter]: alpha ? 0 : 1,

                                [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 1 - alpha,
                            })),
                        }}
                    >
                        <div className="icon" style={{ backgroundImage: `url(${postsIcon})` }}/>
                        <div className="icon-text">Posts</div>
                        <div className="icon-small-text">&nbsp;</div>
                    </animated.div>

                    <animated.div
                        className={styles.item}
                        style={{
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,

                                [AnimState.SearchToCourts]: alpha,

                                [AnimState.Courts]: 1,
                                [AnimState.CourtsToSearch]: 1 - alpha,
                                [AnimState.CourtsToCalendarProgress]: 1 - alpha,
                                [AnimState.CourtsToCalendar]: 0,
                                [AnimState.CourtsToPlayers]: 1 - alpha,
                                [AnimState.CourtsToFilter]: 1 - alpha,

                                [AnimState.CourtsCalendarToCourtsProgress]: 0,
                                [AnimState.CourtsCalendarToCourts]: 1 - alpha,

                                [AnimState.PLayersPlayersToCourts]: alpha,
                            })),
                        }}
                    >
                        <div className="icon" style={{ backgroundImage: `url(${playersIcon})` }}/>
                        <div className="icon-text" style={{ fontWeight: query.mode === 'players' ? 'bold' : undefined }}>Our Players</div>
                        <div className="icon-small-text">
                            {query.mode === 'players' ? `${item?.value?.players?.length || 0} total` : ''}
                            &nbsp;
                        </div>
                    </animated.div>
                </animated.div>

                <animated.div
                    className={styles.col}
                    style={{
                        transform: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.SearchToPlayers]: `translate(0, ${lerp(0, -12, alpha)}px)`,

                            [AnimState.CourtsToPlayers]: `translate(0, ${lerp(0, -12, alpha)}px)`,

                            [AnimState.Players]: `translate(0, ${lerp(0, -12, 1)}px)`,
                            [AnimState.PlayersCourtsToCourts]: `translate(0, ${lerp(-12, 0, alpha)}px)`,
                            [AnimState.PLayersPlayersToPlayers]: `translate(0, ${lerp(0, -12, 1)}px)`,
                            [AnimState.PLayersPlayersToPlayersBack]: `translate(0, ${lerp(0, -12, 1)}px)`,
                            [AnimState.PLayersPlayersToCourts]: `translate(0, ${lerp(0, -12, 1 - alpha)}px)`,
                            [AnimState.PlayersPlayersToCalendarProgress]: `translate(0, ${lerp(0, -12, 1 - alpha)}px)`,
                            [AnimState.PlayersPlayersToFilter]: `translate(0, ${lerp(0, -12, 1)}px)`,
                            [AnimState.PlayersCourtsToFilter]: `translate(0, ${lerp(0, -12, 1)}px)`,

                            [AnimState.PlayersPlayersCalendarToPlayersPlayers]: `translate(0, ${lerp(0, -12, 1 - alpha)}px)`,
                        })),
                        cursor: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.Players]: 'default',
                            [AnimState.PLayersPlayersToPlayers]: 'default',
                            [AnimState.PLayersPlayersToPlayersBack]: 'default',
                            [AnimState.PLayersPlayersToCourts]: 'default',
                            [AnimState.PlayersPlayersToCalendarProgress]: 'default',
                            [AnimState.PlayersPlayersToCalendar]: 'default',
                            [AnimState.PlayersPlayersToFilter]: 'default',
                            [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: 'default',
                            [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 'default',
                            [AnimState.PlayersCourtsToCourts]: 'default',
                            [AnimState.PlayersCourtsToFilter]: 'default',
                        })),
                    }}
                    onClick={() => {
                        if (page === 'courts' && !query.calendar) {
                            history.push({
                                search: qs.stringify({
                                    mode: 'coaches',
                                }),
                            });
                        }
                    }}
                    role="presentation"
                >
                    <animated.div
                        className={styles.item}
                        style={{
                            position: 'absolute',
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,

                                [AnimState.SearchToPlayers]: alpha,

                                [AnimState.CourtsToPlayers]: alpha,

                                [AnimState.Players]: 1,
                                [AnimState.PlayersCourtsToCourts]: 1 - alpha,
                                [AnimState.PLayersPlayersToPlayers]: 1,
                                [AnimState.PLayersPlayersToPlayersBack]: 1,
                                [AnimState.PLayersPlayersToCourts]: 1 - alpha,
                                [AnimState.PlayersPlayersToCalendarProgress]: 1 - alpha,
                                [AnimState.PlayersPlayersToFilter]: alpha ? 0 : 1,
                                [AnimState.PlayersCourtsToFilter]: alpha ? 0 : 1,

                                [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 1 - alpha,
                            })),
                        }}
                    >
                        <div className="icon"/>
                        <div className="icon-text" style={{ fontWeight: 'bold' }}>Favorites</div>
                        <div className="icon-small-text">&nbsp;</div>
                    </animated.div>

                    <animated.div
                        className={styles.item}
                        style={{
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,

                                [AnimState.SearchToCourts]: alpha,

                                [AnimState.Courts]: 1,
                                [AnimState.CourtsToSearch]: 1 - alpha,
                                [AnimState.CourtsToCalendarProgress]: 1 - alpha,
                                [AnimState.CourtsToCalendar]: 0,
                                [AnimState.CourtsToPlayers]: 1 - alpha,
                                [AnimState.CourtsToFilter]: 1 - alpha,

                                [AnimState.CourtsCalendar]: 0,
                                [AnimState.CourtsCalendarToCourtsProgress]: 0,
                                [AnimState.CourtsCalendarToCourts]: 1 - alpha,

                                [AnimState.PLayersPlayersToCourts]: alpha,
                                [AnimState.PlayersCourtsToCourts]: alpha,
                            })),
                        }}
                    >
                        <div className="icon" style={{ backgroundImage: `url(${coachesIcon})` }}/>
                        <div className="icon-text" style={{ fontWeight: query.mode === 'coaches' ? 'bold' : undefined }}>Our Coaches</div>
                        <div className="icon-small-text">
                            {query.mode === 'coaches' ? `${item?.value?.players?.filter((p: any) => p.role === 'coaches').length || 0} total` : ''}
                            &nbsp;
                        </div>
                    </animated.div>
                </animated.div>

                <animated.div
                    className={styles.col}
                    style={{
                        transform: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.SearchToPlayers]: `translate(${lerp(0, 12, alpha)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), alpha)}px)`,

                            [AnimState.CourtsToPlayers]: `translate(${lerp(0, 12, alpha)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), alpha)}px)`,

                            [AnimState.Players]: `translate(${lerp(0, 12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,
                            [AnimState.PlayersCourtsToCourts]: `translate(${lerp(0, 12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,
                            [AnimState.PLayersPlayersToPlayers]: `translate(${lerp(0, 12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,
                            [AnimState.PLayersPlayersToPlayersBack]: `translate(${lerp(0, 12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,
                            [AnimState.PLayersPlayersToCourts]: `translate(${lerp(0, 12, 1 - alpha)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1 - alpha)}px)`,
                            [AnimState.PlayersPlayersToCalendarProgress]: `translate(${lerp(0, 12, 1 - alpha)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1 - alpha)}px)`,
                            [AnimState.PlayersPlayersToFilter]: `translate(${lerp(0, 12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,
                            [AnimState.PlayersCourtsToFilter]: `translate(${lerp(0, 12, 1)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1)}px)`,

                            [AnimState.PlayersPlayersCalendarToPlayersPlayers]: `translate(${lerp(0, 12, 1 - alpha)}px, ${lerp(0, -((device.isShortScreen ? 0 : 45 + insets.top) + (device.isStandalone ? -10 : 30)), 1 - alpha)}px)`,
                        })),
                        cursor: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.PlayersPlayersToCalendar]: 'default',
                        })),
                    }}
                    role="presentation"
                >
                    <animated.div
                        className={styles.item}
                        style={{
                            position: 'absolute',
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,

                                [AnimState.SearchToPlayers]: alpha,

                                [AnimState.CourtsToPlayers]: alpha,

                                [AnimState.Players]: 1,
                                [AnimState.PlayersCourtsToCourts]: 1 - alpha,
                                [AnimState.PLayersPlayersToPlayers]: 1,
                                [AnimState.PLayersPlayersToPlayersBack]: 1,
                                [AnimState.PLayersPlayersToCourts]: 1 - alpha,
                                [AnimState.PlayersPlayersToCalendarProgress]: 1 - alpha,
                                [AnimState.PlayersPlayersToFilter]: alpha ? 0 : 1,
                                [AnimState.PlayersCourtsToFilter]: alpha ? 0 : 1,

                                [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 1 - alpha,
                            })),
                        }}
                    >
                        <div className="icon" style={{ backgroundImage: `url(${gamesIcon})` }}/>
                        <div className="icon-text">Games</div>
                        <div className="icon-small-text">&nbsp;</div>
                    </animated.div>

                    <animated.div
                        className={styles.item}
                        style={{
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,

                                [AnimState.SearchToCourts]: alpha,

                                [AnimState.Courts]: 1,
                                [AnimState.CourtsToSearch]: 1 - alpha,
                                [AnimState.CourtsToCalendarProgress]: 1 - alpha,
                                [AnimState.CourtsToCalendar]: 0,
                                [AnimState.CourtsToPlayers]: 1 - alpha,
                                [AnimState.CourtsToFilter]: 1 - alpha,

                                [AnimState.CourtsCalendarToCourtsProgress]: 0,
                                [AnimState.CourtsCalendarToCourts]: 1 - alpha,

                                [AnimState.PLayersPlayersToCourts]: alpha,
                            })),
                        }}
                    >
                        <div className="icon" style={{ backgroundImage: `url(${courtsIcon})` }}/>
                        <div className="icon-text">Our Courts</div>
                        <div className="icon-small-text">&nbsp;</div>
                    </animated.div>
                </animated.div>
            </div>

            <div className={styles.row} ref={row2Ref}>
                <animated.div
                    className={styles.col}
                    style={{
                        transform: interpolate([anim.alpha], alpha => (
                            animStateRef.current === AnimState.PlayersCourtsToCourts
                                ? `translate(0, ${lerp(0, rowYRef.current, alpha)}px)`
                                : undefined
                        )),
                        cursor: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.PlayersPlayersToCalendar]: 'default',
                        })),
                    }}
                    onClick={() => {
                        if (page === 'players' && !query.calendar) {
                            history.push({
                                search: qs.stringify({
                                    favorites: 'players',
                                }),
                            });
                        }
                    }}
                    role="presentation"
                >
                    <animated.div
                        className={styles.item}
                        style={{
                            position: 'absolute',
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,

                                [AnimState.PlayersCourtsToCourts]: alpha,
                            })),
                        }}
                    >
                        <div className="icon" style={{ backgroundImage: `url(${playersIcon})` }}/>
                        <div className="icon-text">Our Players</div>
                        <div className="icon-small-text">&nbsp;</div>
                    </animated.div>

                    <animated.div
                        className={styles.item}
                        style={{
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,

                                [AnimState.SearchToPlayers]: alpha,

                                [AnimState.CourtsToPlayers]: alpha,

                                [AnimState.Players]: 1,
                                [AnimState.PlayersCourtsToCourts]: 1 - alpha,
                                [AnimState.PLayersPlayersToPlayers]: 1,
                                [AnimState.PLayersPlayersToPlayersBack]: 1,
                                [AnimState.PLayersPlayersToCourts]: 1 - alpha,
                                [AnimState.PlayersPlayersToCalendarProgress]: 1 - alpha,
                                [AnimState.PlayersPlayersToFilter]: alpha ? 0 : 1,
                                [AnimState.PlayersCourtsToFilter]: alpha ? 0 : 1,

                                [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 1 - alpha,
                            })),
                        }}
                    >
                        <div className="icon"/>
                        <div className="icon-text" style={{ fontWeight: query.favorites === 'players' ? 'bold' : undefined }}>Players</div>
                        <div className="icon-small-text">
                            {query.favorites === 'players' ? `${item?.value?.friends?.length || 0} total` : ''}
                            &nbsp;
                        </div>
                    </animated.div>
                </animated.div>

                <animated.div
                    className={styles.col}
                    style={{
                        transform: interpolate([anim.alpha], alpha => (
                            animStateRef.current === AnimState.CourtsToPlayers
                                ? `translate(0, ${lerp(0, -12, alpha)}px)`
                                : animStateRef.current === AnimState.PLayersPlayersToCourts
                                || animStateRef.current === AnimState.PlayersCourtsToCourts
                                    ? `translate(0, ${lerp(0, -12, 1 - alpha)}px)`
                                    : animStateRef.current === AnimState.PLayersPlayersToPlayers
                                        ? 'translate(0, -12px)'
                                        : undefined
                        )),
                        cursor: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.Players]: 'default',
                            [AnimState.PLayersPlayersToPlayers]: 'default',
                            [AnimState.PLayersPlayersToPlayersBack]: 'default',
                            [AnimState.PLayersPlayersToCourts]: 'default',
                            [AnimState.PlayersPlayersToCalendarProgress]: 'default',
                            [AnimState.PlayersPlayersToCalendar]: 'default',
                            [AnimState.PlayersPlayersToFilter]: 'default',
                            [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: 'default',
                            [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 'default',
                            [AnimState.PlayersCourtsToCourts]: 'default',
                            [AnimState.PlayersCourtsToFilter]: 'default',
                        })),
                    }}
                    role="presentation"
                >
                    <animated.div
                        className={styles.item}
                        style={{
                            position: 'absolute',
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,
                            })),
                        }}
                    >
                        <div className="icon"/>
                        <div className="icon-text" style={{ fontWeight: 'bold' }}>Favorites</div>
                        <div className="icon-small-text">&nbsp;</div>
                    </animated.div>

                    <animated.div
                        className={styles.item}
                        style={{
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,
                            })),
                        }}
                    >
                        <div className="icon" style={{ backgroundImage: `url(${coachesIcon})` }}/>
                        <div className="icon-text">Our Coaches</div>
                        <div className="icon-small-text">&nbsp;</div>
                    </animated.div>
                </animated.div>

                <animated.div
                    className={styles.col}
                    style={{
                        transform: interpolate([anim.alpha], alpha => (
                            animStateRef.current === AnimState.PlayersCourtsToCourts
                                ? `translate(0, ${lerp(0, rowYRef.current, alpha)}px)`
                                : undefined
                        )),
                        cursor: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.PlayersPlayersToCalendar]: 'default',
                        })),
                    }}
                    onClick={() => {
                        if (page === 'players' && !query.calendar) {
                            history.push({
                                search: qs.stringify({
                                    favorites: 'courts',
                                }),
                            });
                        }
                    }}
                    role="presentation"
                >
                    <animated.div
                        className={styles.item}
                        style={{
                            position: 'absolute',
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,

                                [AnimState.PlayersCourtsToCourts]: alpha,
                            })),
                        }}
                    >
                        <div className="icon" style={{ backgroundImage: `url(${courtsIcon})` }}/>
                        <div className="icon-text">Our Courts</div>
                        <div className="icon-small-text">&nbsp;</div>
                    </animated.div>

                    <animated.div
                        className={styles.item}
                        style={{
                            opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 0,

                                [AnimState.SearchToPlayers]: alpha,

                                [AnimState.CourtsToPlayers]: alpha,

                                [AnimState.Players]: 1,
                                [AnimState.PlayersCourtsToCourts]: 1 - alpha,
                                [AnimState.PLayersPlayersToPlayers]: 1,
                                [AnimState.PLayersPlayersToPlayersBack]: 1,
                                [AnimState.PLayersPlayersToCourts]: 1 - alpha,
                                [AnimState.PlayersPlayersToCalendarProgress]: 1 - alpha,
                                [AnimState.PlayersPlayersToFilter]: alpha ? 0 : 1,
                                [AnimState.PlayersCourtsToFilter]: alpha ? 0 : 1,

                                [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 1 - alpha,
                            })),
                        }}
                    >
                        <div className="icon"/>
                        <div className="icon-text" style={{ fontWeight: query.favorites === 'courts' ? 'bold' : undefined }}>Courts</div>
                        <div className="icon-small-text">
                            {query.favorites === 'courts' ? `${item?.value?.courts?.length || 0} total` : ''}
                            &nbsp;
                        </div>
                    </animated.div>
                </animated.div>
            </div>
        </div>
    );
}
